import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useCreateHolderMutation,
  useUpdateHolderMutation,
  useCreateNewRequisiteMutation,
  useUpdateNewRequisiteMutation,
  useGetActiveCurrenciesQuery,
  useGetBanksQuery,
  useGetActiveMobileOperatorsQuery, // VP-197
  useGetGeoLocationsQuery,
  useGetAvailablePaymentMethodsQuery,
  useGetTraderHoldersQuery,
  useGetRequisiteByIdQuery,
} from "../store/trader-api";

const SidebarOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 320px;
  background: var(--bg-card-hover);
  display: ${(props) => (props.isOpen ? "block" : "none")};
  z-index: 150;
  cursor: pointer;

  @media (max-width: 768px) {
    left: 0;
    background: var(--bg-modal-overlay);
    z-index: 9998;
  }
`;

const SidebarContent = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 320px;
  max-width: 92vw;
  background: var(--bg-card-hover);
  color: var(--text-primary);
  padding: 1.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  transform: ${(props) =>
    props.isOpen ? "translateX(0)" : "translateX(-100%)"};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 300;

  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    height: 100dvh;
    padding: 0;
    background: var(--bg-card);
    z-index: 9999;
    justify-content: flex-start;
    transform: ${(props) =>
      props.isOpen ? "translateY(0)" : "translateY(100%)"};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem 1rem 0.75rem;
    margin-bottom: 0;
    border-bottom: 1px solid var(--border-divider);
    background: var(--bg-primary);
    flex-shrink: 0;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1;
  background: transparent;
  border-radius: 0.5rem;
  padding: 0.25rem;
`;

const CloseButton = styled.button`
  background: var(--bg-glass);
  border: 1px solid var(--border-light);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: var(--alert-error-bg);
    color: var(--accent-error);
    border-color: var(--alert-error-border);
    transform: scale(1.05);
  }
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${(props) =>
    props.active ? "var(--nav-active-bg)" : "transparent"};
  color: ${(props) => (props.active ? "var(--nav-active-text)" : "var(--text-primary)")};
  font-weight: 600;
  cursor: pointer;
  border-radius: 0.375rem;
  transition: all 0.2s;
  font-size: 0.75rem;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--accent-primary)" : "var(--bg-hover)"};
    color: ${(props) => (props.active ? "var(--nav-active-text)" : "var(--text-primary)")};
  }
`;

const FormContainer = styled.div`
  max-height: 75vh;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  background: transparent;

  @media (max-width: 768px) {
    max-height: none;
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    -webkit-overflow-scrolling: touch;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: transparent;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  width: 100%;
  background: transparent;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid
    ${(props) => (props.hasError ? "var(--accent-error)" : "var(--border-light)")};
  border-radius: 0.5rem;
  font-size: 0.75rem;
  background: var(--bg-glass);
  color: var(--text-primary);
  backdrop-filter: blur(12px);
  transition: all 0.2s;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  box-shadow: ${(props) =>
    props.hasError ? "0 0 0 2px var(--accent-error-border)" : "none"};

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "var(--accent-error)" : "var(--accent-primary)")};
    background: var(--bg-card);
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.hasError ? "var(--accent-error-border)" : "var(--accent-primary-border)"};
  }

  &:disabled {
    background: var(--bg-input-disabled);
    color: var(--text-disabled);
  }

  @media (max-width: 768px) {
    border-color: ${(props) => (props.hasError ? "var(--accent-error)" : "var(--border-input)")};
    background: var(--bg-input);
    backdrop-filter: none;
    padding: 0.625rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid
    ${(props) => (props.hasError ? "var(--accent-error)" : "var(--border-light)")};
  border-radius: 0.5rem;
  font-size: 0.75rem;
  background: var(--bg-glass);
  color: var(--text-primary);
  backdrop-filter: blur(12px);
  transition: all 0.2s;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  box-shadow: ${(props) =>
    props.hasError ? "0 0 0 2px var(--accent-error-border)" : "none"};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "var(--accent-error)" : "var(--accent-primary)")};
    background: var(--bg-card);
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.hasError ? "var(--accent-error-border)" : "var(--accent-primary-border)"};
  }

  &:disabled {
    background: var(--bg-input-disabled);
    color: var(--text-disabled);
  }

  option {
    background: var(--bg-card);
    color: var(--text-primary);
  }

  option:first-child {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    border-color: ${(props) => (props.hasError ? "var(--accent-error)" : "var(--border-input)")};
    background: var(--bg-input);
    backdrop-filter: none;
    padding: 0.625rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    background: var(--bg-card);
    padding: 0.75rem 0;
    margin-top: 1rem;
    border-top: 1px solid var(--border-divider);
  }
`;

const SubmitButton = styled.button`
  background: var(--accent-primary);
  color: #ffffff;
  border: 1px solid var(--accent-primary-border);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(12px);
  font-size: 0.875rem;
  flex: 1;

  &:hover {
    background: var(--accent-primary-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--bg-secondary);
    border-color: var(--border-color);
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CancelButton = styled.button`
  background: var(--bg-glass);
  color: var(--text-primary);
  border: 1px solid var(--border-light);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(12px);
  font-size: 0.875rem;
  flex: 1;

  &:hover {
    background: var(--bg-card);
    border-color: var(--border-color);
    transform: translateY(-1px);
  }
`;

const ErrorText = styled.span`
  color: var(--accent-error);
  font-size: 0.65rem;
  opacity: 0.9;
`;

const Label = styled.label`
  font-size: 0.65rem;
  color: var(--text-primary);
  font-weight: 600;
  margin-left: 0.25rem;
  margin-bottom: 0.125rem;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
  }
`;

const InfoIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  color: var(--text-secondary);
  cursor: help;
  position: relative;
  vertical-align: middle;
  
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 5px;
    padding: 0.5rem;
    background: var(--bg-tooltip);
    color: var(--text-tooltip);
    font-size: 0.7rem;
    border-radius: 0.25rem;
    white-space: normal;
    max-width: 250px;
    width: max-content;
    text-align: center;
    z-index: 9999;
    font-weight: normal;
    pointer-events: none;
  }
`;

const UsageText = styled.div`
  font-size: 0.65rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
  margin-left: 0.25rem;
`;

const InfoIconSVG = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

interface RequisitesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  traderId: string;
  editingHolder?: any; // Данные держателя для редактирования
  editingRequisite?: any; // Данные реквизита для редактирования
}

type TabType = "holder" | "requisite";

// VP-205: phoneNumber и countryId убраны из формы добавления держателя.
// Телефон теперь привязан к реквизиту, страна выбирается на самом реквизите
// для трансгран-методов.
interface HolderFormData {
  firstName: string;
  middleName?: string;
  lastName: string;
  tag?: string;
  isActive?: boolean;
}

interface RequisiteFormData {
  holderId: string; // Added holderId to RequisiteFormData
  method: string;
  bankId: string;       // Mandatory для всех методов кроме MOBCOM
  mobileOperatorId?: string; // VP-197: для метода MOBCOM
  currencyCode: string; // Mandatory
  // VP-205: страна реквизита — заполняется только для трансгран-методов
  // (CROSSBORDER_PHONE / CROSSBORDER_CARD).
  countryCode?: string;
  cardNumber?: string;
  holderName?: string;
  sbpPhoneNumber?: string;
  mobcomPhoneNumber?: string; // VP-197
  // VP-201: трансгран-методы
  crossborderPhoneNumber?: string;
  crossborderCardNumber?: string;
  accountNumber?: string;
  bankIdCode?: string;
  recipientName?: string;
  minAmount: string;
  maxAmount: string;
  maxTotalVolumeLimit?: string;
  paymentLimit?: string;
  dailyPaymentLimit?: string;
  cooldownMin?: string;
  dailyLimit?: string;
  tag?: string;
  isActive?: boolean;
  maxParallelOrders?: string;
}

// VP-201: список двухбуквенных кодов стран, поддержанных в трансгране.
// Должен совпадать с CROSSBORDER_COUNTRY_CODES в бэкенд-валидаторах.
const CROSSBORDER_COUNTRY_CODES = ["AM", "TJ", "UZ", "AB"] as const;
const isCrossborderCountryCode = (code?: string | null) =>
  !!code && (CROSSBORDER_COUNTRY_CODES as readonly string[]).includes(code);

const RequisitesSidebar: React.FC<RequisitesSidebarProps> = ({
  isOpen,
  onClose,
  traderId,
  editingHolder,
  editingRequisite,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("holder");

  // Определяем режим работы
  const isEditingHolder = !!editingHolder;
  const isEditingRequisite = !!editingRequisite;

  // API hooks
  const { data: currencies = [] } = useGetActiveCurrenciesQuery();
  const { data: banks = [] } = useGetBanksQuery();
  const { data: mobileOperators = [] } = useGetActiveMobileOperatorsQuery(); // VP-197
  const { data: geoLocations = [] } = useGetGeoLocationsQuery();
  const { data: paymentMethods = [] } = useGetAvailablePaymentMethodsQuery();
  const { data: holdersData } = useGetTraderHoldersQuery({ traderId });

  // Загружаем полные данные реквизита для редактирования
  const { data: fullRequisiteData } = useGetRequisiteByIdQuery(
    editingRequisite?.id || "",
    {
      skip: !editingRequisite?.id,
    }
  );

  const [createHolder, { isLoading: isCreatingHolder }] =
    useCreateHolderMutation();
  const [updateHolder, { isLoading: isUpdatingHolder }] =
    useUpdateHolderMutation();
  const [createRequisite, { isLoading: isCreatingRequisite }] =
    useCreateNewRequisiteMutation();
  const [updateRequisite, { isLoading: isUpdatingRequisite }] =
    useUpdateNewRequisiteMutation();

  const holders = holdersData?.data || [];

  // Forms
  const holderForm = useForm<HolderFormData>({
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      tag: "",
      isActive: true,
    },
  });

  const requisiteForm = useForm<RequisiteFormData>({
    defaultValues: {
      holderId: "",
      method: "",
      cardNumber: "",
      holderName: "",
      minAmount: "",
      maxAmount: "",
      maxTotalVolumeLimit: "",
      paymentLimit: "",
      dailyPaymentLimit: "",
      cooldownMin: "",
      dailyLimit: "",
      tag: "",
      isActive: true,
      maxParallelOrders: "",
      bankId: "",
      // VP-197: без явного "" в defaultValues нативный <select> с
      // value=undefined визуально выбирает первый non-hidden option,
      // но onChange не вызывается — RHF-state остаётся пустым и сабмит падает.
      mobileOperatorId: "",
      mobcomPhoneNumber: "",
      // VP-201: ТГ-поля
      crossborderPhoneNumber: "",
      crossborderCardNumber: "",
      sbpPhoneNumber: "",
      accountNumber: "",
      bankIdCode: "",
      recipientName: "",
      currencyCode: "RUB",
      // VP-205: страна реквизита (только для трансгран-методов)
      countryCode: "",
    },
  });

  // Reset forms when sidebar opens/closes
  useEffect(() => {
    if (!isOpen) {
      holderForm.reset();
      requisiteForm.reset();
    }
  }, [isOpen, holderForm, requisiteForm]);

  // Заполняем форму при редактировании держателя.
  // VP-205: phoneNumber/countryId больше не входят в форму держателя.
  useEffect(() => {
    if (isEditingHolder && editingHolder && isOpen) {
      setActiveTab("holder");
      holderForm.reset({
        firstName: editingHolder.firstName,
        lastName: editingHolder.lastName,
        middleName: editingHolder.middleName || "",
        tag: editingHolder.tag || "",
        isActive: editingHolder.isActive,
      });
    }
  }, [isEditingHolder, editingHolder, isOpen, holderForm]);

  // Заполняем форму при редактировании реквизита
  useEffect(() => {
    if (isEditingRequisite && fullRequisiteData && isOpen) {
      setActiveTab("requisite");
      const limits = fullRequisiteData.limits || [];

      requisiteForm.reset({
        holderId: fullRequisiteData.holderId || "",
        method: fullRequisiteData.method || "",
        // Теперь используем данные из fullRequisiteData, которые содержат полную информацию
        cardNumber: fullRequisiteData.cardNumber || "",
        holderName: fullRequisiteData.holderName || "",
        sbpPhoneNumber: fullRequisiteData.sbpPhoneNumber || "",
        // VP-197
        mobcomPhoneNumber: (fullRequisiteData as any).mobcomPhoneNumber || "",
        mobileOperatorId: (fullRequisiteData as any).mobileOperatorId || "",
        // VP-201
        crossborderPhoneNumber:
          (fullRequisiteData as any).crossborderPhoneNumber || "",
        crossborderCardNumber:
          (fullRequisiteData as any).crossborderCardNumber || "",
        accountNumber: fullRequisiteData.accountNumber || "",
        bankIdCode: fullRequisiteData.bankIdCode || "",
        recipientName: fullRequisiteData.recipientName || "",
        maxTotalVolumeLimit:
          fullRequisiteData.maxTotalVolumeLimit?.toString() || "",
        cooldownMin:
          fullRequisiteData.cooldownSec && fullRequisiteData.cooldownSec > 0
            ? Math.floor(fullRequisiteData.cooldownSec / 60).toString()
            : "",
        dailyLimit: fullRequisiteData.dailyLimit?.toString() || "",
        paymentLimit: fullRequisiteData.paymentLimit?.toString() || "",
        dailyPaymentLimit: fullRequisiteData.dailyPaymentLimit?.toString() || "",
        tag: fullRequisiteData.tag || "",
        isActive:
          fullRequisiteData.isActive !== undefined
            ? fullRequisiteData.isActive
            : true,
        maxParallelOrders: fullRequisiteData.maxParallelOrders?.toString() || "",
        minAmount: limits[0]?.minAmount?.toString() || "",
        maxAmount: limits[0]?.maxAmount?.toString() || "",
        bankId: fullRequisiteData.bankId?.toString() || "",
        currencyCode: fullRequisiteData.currencyCode || "RUB",
        // VP-205: страна реквизита (только для трансгран)
        countryCode: (fullRequisiteData as any).countryCode || "",
      });
    }
  }, [isEditingRequisite, fullRequisiteData, isOpen, requisiteForm]);

  const onSubmitHolder = async (data: HolderFormData) => {
    try {
      if (isEditingHolder) {
        await updateHolder({
          holderId: editingHolder.id,
          holderData: {
            ...data,
          },
        }).unwrap();
        toast.success("Держатель обновлен");
        holderForm.reset();
        onClose();
        return;
      }

      // Создание нового держателя: после успеха модалка не закрывается,
      // а переключается на вкладку «Добавить реквизит» с pre-filled holderId.
      // Создание реквизита — естественное продолжение флоу: без хотя бы
      // одного реквизита держатель ничего не делает.
      const newHolder = await createHolder({
        traderId,
        holderData: {
          ...data,
        },
      }).unwrap();
      toast.success("Держатель создан");
      holderForm.reset();
      // Сбрасываем форму реквизита к defaults и подставляем holderId нового держателя.
      requisiteForm.reset();
      requisiteForm.setValue("holderId", newHolder.id, {
        shouldValidate: false,
      });
      setActiveTab("requisite");
    } catch (error: any) {
      toast.error(error?.data?.message || "Ошибка при сохранении держателя");
    }
  };

  const onSubmitRequisite = async (data: RequisiteFormData) => {
    // Преобразуем строки в числа
    const minAmount = parseFloat(data.minAmount) || 0;
    const maxAmount = parseFloat(data.maxAmount) || 0;
    const maxTotalVolumeLimit = data.maxTotalVolumeLimit
      ? parseFloat(data.maxTotalVolumeLimit)
      : null;
    const cooldownSec =
      data.cooldownMin && data.cooldownMin.trim() !== ""
        ? parseInt(data.cooldownMin) * 60
        : null; // Make cooldownSec null if not provided or cleared
    const dailyLimit = data.dailyLimit
      ? parseFloat(data.dailyLimit)
      : null;

    // Валидация лимитов: max не может быть меньше min
    if (maxAmount < minAmount) {
      toast.error("Максимальная сумма не может быть меньше минимальной");
      return;
    }

    if (minAmount <= 0 || maxAmount <= 0) {
      toast.error("Суммы должны быть больше нуля");
      return;
    }

    // Примечание: поля банк, метод, номер карты/телефона/счета защищены disabled={isEditingRequisite}
    // Остальные поля (лимиты, интервал, название и т.д.) можно менять на активном реквизите

    try {
      // Подготавливаем данные для отправки
      const requisiteData: any = {
        method: data.method, // Method is always required for creation and update
        isActive: data.isActive,
        tag: data.tag || undefined, // Only include if present
      };

      // bankId/mobileOperatorId и currencyCode только при создании, не при обновлении!
      // VP-197: для метода MOBCOM bankId не задаётся, вместо него используется mobileOperatorId
      // VP-205: countryCode передаём только при создании трансгран-реквизита.
      if (!isEditingRequisite) {
        if (data.method === "MOBCOM") {
          requisiteData.mobileOperatorId = data.mobileOperatorId;
        } else {
          requisiteData.bankId = Number(data.bankId);
        }
        requisiteData.currencyCode = data.currencyCode;
        if (
          data.method === "CROSSBORDER_PHONE" ||
          data.method === "CROSSBORDER_CARD"
        ) {
          requisiteData.countryCode = data.countryCode;
        }
      }

      // Include optional fields directly to allow nulling them out
      requisiteData.maxTotalVolumeLimit = maxTotalVolumeLimit;
      requisiteData.cooldownSec = cooldownSec;
      requisiteData.dailyLimit = dailyLimit;

      // Лимит количества платежей (общий)
      requisiteData.paymentLimit = data.paymentLimit && data.paymentLimit.trim() !== ""
        ? parseInt(data.paymentLimit)
        : null;

      // Лимит количества платежей в сутки
      requisiteData.dailyPaymentLimit = data.dailyPaymentLimit && data.dailyPaymentLimit.trim() !== ""
        ? parseInt(data.dailyPaymentLimit)
        : null;

      // Макс. параллельных сделок
      requisiteData.maxParallelOrders = data.maxParallelOrders && data.maxParallelOrders.trim() !== ""
        ? parseInt(data.maxParallelOrders)
        : null;

      // Add fields specific to the payment method
      // Fields like cardNumber, sbpPhoneNumber, accountNumber are immutable after creation
      if (!isEditingRequisite) {
        switch (data.method) {
          case "CARD_NUMBER":
            requisiteData.cardNumber = data.cardNumber || undefined;
            requisiteData.holderName = data.holderName || undefined;
            break;
          case "SBP":
            requisiteData.sbpPhoneNumber = data.sbpPhoneNumber || undefined;
            break;
          case "MOBCOM":
            // VP-197: телефон MOBCOM (mobileOperatorId уже выставлен выше)
            requisiteData.mobcomPhoneNumber =
              data.mobcomPhoneNumber || undefined;
            break;
          case "CROSSBORDER_PHONE":
            // VP-201: локальный номер телефона ТГ
            requisiteData.crossborderPhoneNumber =
              data.crossborderPhoneNumber || undefined;
            requisiteData.holderName = data.holderName || undefined;
            break;
          case "CROSSBORDER_CARD":
            // VP-201: локальный номер карты ТГ
            requisiteData.crossborderCardNumber =
              data.crossborderCardNumber || undefined;
            requisiteData.holderName = data.holderName || undefined;
            break;
          case "ACCOUNT_TRANSFER":
            requisiteData.accountNumber = data.accountNumber || undefined;
            requisiteData.bankIdCode = data.bankIdCode || undefined;
            requisiteData.recipientName = data.recipientName || undefined;
            break;
        }
      } else {
        // Even when editing, holderName or recipientName might be allowed to update,
        // but currently they are disabled on the UI. We skip sending them to prevent data corruption.
      }

      // Limits
      requisiteData.limits = [
        {
          method: data.method,
          minAmount,
          maxAmount,
          currency: data.currencyCode, // Use selected currency
          isActive: true,
        },
      ];

      if (isEditingRequisite) {
        await updateRequisite({
          requisiteId: editingRequisite.id,
          requisiteData,
        }).unwrap();
        toast.success("Реквизит обновлен");
      } else {
        await createRequisite({
          holderId: data.holderId,
          requisiteData,
        }).unwrap();
        toast.success("Реквизит создан");
      }
      requisiteForm.reset();
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || "Ошибка при сохранении реквизита");
    }
  };

  const watchedMethod = requisiteForm.watch("method");
  const watchedCountryCode = requisiteForm.watch("countryCode");

  // VP-201 + VP-205: ТГ-логика — флаг isCrossborder на методе плюс выбранная
  // на форме страна (countryCode). Раньше страна бралась с холдера, теперь
  // выбирается на самом реквизите.
  const selectedMethodMeta = paymentMethods.find(
    (m) => m.method === watchedMethod
  );
  const isCrossborderMethod = !!selectedMethodMeta?.isCrossborder;

  const selectedRequisiteCountry = isCrossborderCountryCode(watchedCountryCode)
    ? watchedCountryCode
    : undefined;

  // Фильтр банков:
  //  - для трансгран-методов (CROSSBORDER_PHONE / CROSSBORDER_CARD) — только
  //    банки выбранной страны (AM/TJ/UZ/AB), т.е. countryCode != RUS;
  //  - для всех остальных «локальных» методов (SBP/CARD_NUMBER/...) — только
  //    российские банки (countryCode === 'RUS'), чтобы трейдер случайно не
  //    привязал не-RUS банк к локальному реквизиту.
  //  - MOBCOM банк не использует — селект банков для него не показывается.
  const banksForSelect = isCrossborderMethod
    ? banks.filter(
        (b: any) =>
          !!selectedRequisiteCountry && b.countryCode === selectedRequisiteCountry
      )
    : banks.filter((b: any) => b.countryCode === "RUS");

  // При смене метода/страны сбрасываем выбранный bankId, если он больше
  // не входит в актуальный список banksForSelect (например, переключение
  // SBP -> CROSSBORDER_CARD, где старый RUS-банк уже не валиден).
  // В режиме редактирования реквизита поля метод/банк/страна disabled,
  // поэтому авто-сброс там не нужен.
  useEffect(() => {
    if (isEditingRequisite) return;
    const currentBankId = requisiteForm.getValues("bankId");
    if (!currentBankId) return;
    const stillFits = banksForSelect.some(
      (b: any) => String(b.id) === String(currentBankId)
    );
    if (!stillFits) {
      requisiteForm.setValue("bankId", "", { shouldValidate: false });
    }
    // banksForSelect зависит от method/countryCode (а они уже в deps);
    // массив banks стабилен в рамках одного RTK Query кэша.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMethod, watchedCountryCode, isCrossborderMethod, isEditingRequisite]);

  // VP-205: при смене метода с трансграна на не-трансгран — гасим countryCode,
  // чтобы не отправить его на бэк случайно.
  useEffect(() => {
    if (!isCrossborderMethod) {
      const cur = requisiteForm.getValues("countryCode");
      if (cur) requisiteForm.setValue("countryCode", "", { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMethod, isCrossborderMethod]);

  return (
    <>
      <SidebarOverlay isOpen={isOpen} />
      <SidebarContent isOpen={isOpen}>
        <SidebarHeader>
          <TabsContainer>
            <Tab
              active={activeTab === "holder"}
              onClick={() => {
                setActiveTab("holder");
                holderForm.reset();
              }}
            >
              Добавить держателя
            </Tab>
            <Tab
              active={activeTab === "requisite"}
              onClick={() => {
                setActiveTab("requisite");
                requisiteForm.reset();
              }}
            >
              Добавить реквизит
            </Tab>
          </TabsContainer>
          <CloseButton onClick={onClose} title="Закрыть">
            ×
          </CloseButton>
        </SidebarHeader>

        {activeTab === "holder" && (
          <FormContainer>
            <Form onSubmit={holderForm.handleSubmit(onSubmitHolder)} autoComplete="off">
              <FormGroup>
                <Label>Имя</Label>
                <Controller
                  name="firstName"
                  control={holderForm.control}
                  rules={{
                    required: "Имя обязательно",
                    pattern: {
                      value: /^[a-zA-Zа-яА-ЯёЁ\s\-]+$/,
                      message:
                        "Имя может содержать только буквы, пробелы и дефисы",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      hasError={!!holderForm.formState.errors.firstName}
                    />
                  )}
                />
                {holderForm.formState.errors.firstName && (
                  <ErrorText>
                    {holderForm.formState.errors.firstName.message}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Фамилия</Label>
                <Controller
                  name="lastName"
                  control={holderForm.control}
                  rules={{
                    required: "Фамилия обязательна",
                    pattern: {
                      value: /^[a-zA-Zа-яА-ЯёЁ\s\-]+$/,
                      message:
                        "Фамилия может содержать только буквы, пробелы и дефисы",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      hasError={!!holderForm.formState.errors.lastName}
                    />
                  )}
                />
                {holderForm.formState.errors.lastName && (
                  <ErrorText>
                    {holderForm.formState.errors.lastName.message}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Отчество</Label>
                <Controller
                  name="middleName"
                  control={holderForm.control}
                  rules={{
                    pattern: {
                      value: /^[a-zA-Zа-яА-ЯёЁ\s\-]+$/,
                      message:
                        "Отчество может содержать только буквы, пробелы и дефисы",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      hasError={!!holderForm.formState.errors.middleName}
                    />
                  )}
                />
                {holderForm.formState.errors.middleName && (
                  <ErrorText>
                    {holderForm.formState.errors.middleName.message}
                  </ErrorText>
                )}
              </FormGroup>

              {/* VP-205: «Номер телефона» убран из формы держателя — телефон
                  теперь привязан к самому реквизиту. */}


              {/* VP-205: поле «Страна» убрано из формы держателя — страна
                  выбирается на реквизите для трансгран-методов. */}

              <FormGroup>
                <Label>Название</Label>
                <Controller
                  name="tag"
                  control={holderForm.control}
                  render={({ field }) => (
                    <Input {...field} placeholder="(Опционально)" />
                  )}
                />
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={onClose}>
                  Отмена
                </CancelButton>
                <SubmitButton
                  type="submit"
                  disabled={isCreatingHolder || isUpdatingHolder}
                >
                  {isCreatingHolder || isUpdatingHolder
                    ? isEditingHolder
                      ? "Сохранение..."
                      : "Создание..."
                    : isEditingHolder
                      ? "Сохранить"
                      : "Создать держателя"}
                </SubmitButton>
              </ButtonGroup>
            </Form>
          </FormContainer>
        )}

        {activeTab === "requisite" && (
          <FormContainer>
            <Form onSubmit={requisiteForm.handleSubmit(onSubmitRequisite)} autoComplete="off">
              <FormGroup>
                <Label>Держатель</Label>
                <Controller
                  name="holderId"
                  control={requisiteForm.control}
                  rules={{ required: "Держатель обязателен" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      hasError={!!requisiteForm.formState.errors.holderId}
                    >
                      <option value="" disabled hidden>
                        Выберите...
                      </option>
                      {holders.map((holder) => (
                        <option key={holder.id} value={holder.id}>
                          {holder.firstName} {holder.lastName}
                          {holder.middleName ? ` ${holder.middleName}` : ""}
                          {holder.tag ? ` (${holder.tag})` : ""}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                {requisiteForm.formState.errors.holderId && (
                  <ErrorText>
                    {requisiteForm.formState.errors.holderId.message}
                  </ErrorText>
                )}
              </FormGroup>



              {/* VP-197: метод выбираем первым, чтобы дальше показать
                  релевантный селект — банк (для SBP/CARD/...) или
                  мобильного оператора (для MOBCOM). */}
              <FormGroup>
                <Label>Метод</Label>
                <Controller
                  name="method"
                  control={requisiteForm.control}
                  rules={{ required: "Метод обязателен" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      hasError={!!requisiteForm.formState.errors.method}
                      disabled={isEditingRequisite}
                      title={isEditingRequisite ? "Метод нельзя изменить после создания реквизита" : ""}
                    >
                      <option value="" disabled hidden>
                        Выберите...
                      </option>
                      {paymentMethods.map((method) => (
                        <option key={method.method} value={method.method}>
                          {method.displayName}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </FormGroup>

              {/* VP-205: для трансгран-методов страна теперь выбирается
                  на самом реквизите (не наследуется от холдера). Список —
                  ровно те, что поддерживаются на бэке (AM/TJ/UZ/AB).
                  Имя страны для отображения берём из geoLocations по коду. */}
              {isCrossborderMethod && (
                <FormGroup>
                  <Label>Страна</Label>
                  <Controller
                    name="countryCode"
                    control={requisiteForm.control}
                    rules={{
                      validate: (value) =>
                        !isCrossborderMethod ||
                        isCrossborderCountryCode(value) ||
                        "Страна обязательна для трансгран-методов",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        hasError={!!requisiteForm.formState.errors.countryCode}
                        disabled={isEditingRequisite}
                        title={
                          isEditingRequisite
                            ? "Страну нельзя изменить после создания реквизита"
                            : ""
                        }
                      >
                        <option value="" disabled hidden>
                          Выберите...
                        </option>
                        {CROSSBORDER_COUNTRY_CODES.map((code) => {
                          const geo = geoLocations.find(
                            (g: any) => g.code === code
                          );
                          return (
                            <option key={code} value={code}>
                              {geo ? `${geo.name} (${code})` : code}
                            </option>
                          );
                        })}
                      </Select>
                    )}
                  />
                  {requisiteForm.formState.errors.countryCode && (
                    <ErrorText>
                      {requisiteForm.formState.errors.countryCode.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {/* VP-197: банк показываем для всех методов кроме MOBCOM */}
              {watchedMethod !== "MOBCOM" && (
                <FormGroup>
                  <Label>Банк</Label>
                  <Controller
                    name="bankId"
                    control={requisiteForm.control}
                    rules={{
                      validate: (value) =>
                        watchedMethod === "MOBCOM" ||
                        !!value ||
                        "Банк обязателен",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        hasError={!!requisiteForm.formState.errors.bankId}
                        disabled={isEditingRequisite}
                        title={isEditingRequisite ? "Банк нельзя изменить после создания реквизита" : ""}
                      >
                        <option value="" disabled hidden>
                          {isCrossborderMethod && !selectedRequisiteCountry
                            ? "Сначала выберите страну (AM/TJ/UZ/AB)"
                            : "Выберите..."}
                        </option>
                        {banksForSelect.map((bank) => (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                            {isCrossborderMethod && bank.countryCode
                              ? ` (${bank.countryCode})`
                              : ""}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                  {requisiteForm.formState.errors.bankId && (
                    <ErrorText>{requisiteForm.formState.errors.bankId.message}</ErrorText>
                  )}
                  {isCrossborderMethod && selectedRequisiteCountry &&
                    banksForSelect.length === 0 && (
                      <ErrorText>
                        В системе нет активных банков для страны {selectedRequisiteCountry}.
                        Попросите администратора добавить банк через админку (Banks → Создать → Страна = {selectedRequisiteCountry}).
                      </ErrorText>
                    )}
                </FormGroup>
              )}

              {/* VP-197: для метода MOBCOM — выбор мобильного оператора вместо банка */}
              {watchedMethod === "MOBCOM" && (
                <FormGroup>
                  <Label>Мобильный оператор</Label>
                  <Controller
                    name="mobileOperatorId"
                    control={requisiteForm.control}
                    rules={{
                      validate: (value) =>
                        watchedMethod !== "MOBCOM" ||
                        !!value ||
                        "Оператор обязателен для метода MOBCOM",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        hasError={
                          !!requisiteForm.formState.errors.mobileOperatorId
                        }
                        disabled={isEditingRequisite}
                        title={
                          isEditingRequisite
                            ? "Оператора нельзя изменить после создания реквизита"
                            : ""
                        }
                      >
                        <option value="" disabled hidden>
                          Выберите оператора...
                        </option>
                        {mobileOperators.map((op) => (
                          <option key={op.id} value={op.id}>
                            {op.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                  {requisiteForm.formState.errors.mobileOperatorId && (
                    <ErrorText>
                      {requisiteForm.formState.errors.mobileOperatorId.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {/* TODO: Поле валюты скрыто до реализации трансграничных переводов.
                  Когда метод = TRANSGOV, показывать выбор валюты. Иначе валюта = по умолчанию из гео. */}

              {/* Поля в зависимости от типа */}
              {watchedMethod === "CARD_NUMBER" && (
                <FormGroup>
                  <Label>Номер карты</Label>
                  <Controller
                    name="cardNumber"
                    control={requisiteForm.control}
                    rules={{
                      required: !isEditingRequisite ? "Номер карты обязателен" : false,
                      pattern: !isEditingRequisite ? {
                        value: /^\d{13,19}$/,
                        message:
                          "Номер карты должен содержать от 13 до 19 цифр",
                      } : undefined,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        hasError={!!requisiteForm.formState.errors.cardNumber}
                        disabled={isEditingRequisite}
                        title={isEditingRequisite ? "Номер карты нельзя изменить после создания" : ""}
                      />
                    )}
                  />
                  {requisiteForm.formState.errors.cardNumber && (
                    <ErrorText>
                      {requisiteForm.formState.errors.cardNumber.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {watchedMethod === "SBP" && (
                <FormGroup>
                  <Label>Номер телефона СБП</Label>
                  <Controller
                    name="sbpPhoneNumber"
                    control={requisiteForm.control}
                    rules={{
                      required: !isEditingRequisite ? "Номер телефона обязателен для СБП" : false,
                      pattern: !isEditingRequisite ? {
                        value:
                          /^(\+7|8)?[\s\-]?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/,
                        message: "Неверный формат номера телефона",
                      } : undefined,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        hasError={
                          !!requisiteForm.formState.errors.sbpPhoneNumber
                        }
                        disabled={isEditingRequisite}
                        title={isEditingRequisite ? "Номер телефона СБП нельзя изменить после создания" : ""}
                      />
                    )}
                  />
                  {requisiteForm.formState.errors.sbpPhoneNumber && (
                    <ErrorText>
                      {requisiteForm.formState.errors.sbpPhoneNumber.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {/* VP-197: телефон для метода MOBCOM */}
              {watchedMethod === "MOBCOM" && (
                <FormGroup>
                  <Label>Номер телефона (мобком)</Label>
                  <Controller
                    name="mobcomPhoneNumber"
                    control={requisiteForm.control}
                    rules={{
                      required: !isEditingRequisite
                        ? "Номер телефона обязателен для MOBCOM"
                        : false,
                      pattern: !isEditingRequisite
                        ? {
                            value:
                              /^(\+7|8)?[\s\-]?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/,
                            message: "Неверный формат номера телефона",
                          }
                        : undefined,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        hasError={
                          !!requisiteForm.formState.errors.mobcomPhoneNumber
                        }
                        disabled={isEditingRequisite}
                        title={
                          isEditingRequisite
                            ? "Номер телефона MOBCOM нельзя изменить после создания"
                            : ""
                        }
                      />
                    )}
                  />
                  {requisiteForm.formState.errors.mobcomPhoneNumber && (
                    <ErrorText>
                      {requisiteForm.formState.errors.mobcomPhoneNumber.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {/* VP-205: локальный номер телефона для CROSSBORDER_PHONE.
                  Подсказка по формату — по выбранной на форме стране
                  (selectedRequisiteCountry). Конкретный паттерн валидируется на сервере. */}
              {watchedMethod === "CROSSBORDER_PHONE" && (
                <FormGroup>
                  <Label>
                    Локальный номер телефона
                    {selectedRequisiteCountry === "AM" && " (AM, формат +374XXXXXXXX)"}
                    {selectedRequisiteCountry === "TJ" && " (TJ, формат +992XXXXXXXXX)"}
                    {selectedRequisiteCountry === "UZ" && " (UZ, формат +998XXXXXXXXX)"}
                    {selectedRequisiteCountry === "AB" && " (AB, формат +7940XXXXXXX)"}
                  </Label>
                  <Controller
                    name="crossborderPhoneNumber"
                    control={requisiteForm.control}
                    rules={{
                      required: !isEditingRequisite
                        ? "Номер телефона обязателен для CROSSBORDER_PHONE"
                        : false,
                      pattern: !isEditingRequisite
                        ? {
                            value: /^\+?[\d\s\-()]+$/,
                            message:
                              "Номер телефона должен состоять из цифр, '+' и разделителей",
                          }
                        : undefined,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={
                          selectedRequisiteCountry === "AM"
                            ? "+374 99 12 34 56"
                            : selectedRequisiteCountry === "TJ"
                            ? "+992 93 123 45 67"
                            : selectedRequisiteCountry === "UZ"
                            ? "+998 90 123 45 67"
                            : selectedRequisiteCountry === "AB"
                            ? "+7 940 123 45 67"
                            : "+CCC..."
                        }
                        hasError={
                          !!requisiteForm.formState.errors.crossborderPhoneNumber
                        }
                        disabled={isEditingRequisite}
                        title={
                          isEditingRequisite
                            ? "Локальный номер нельзя изменить после создания"
                            : ""
                        }
                      />
                    )}
                  />
                  {requisiteForm.formState.errors.crossborderPhoneNumber && (
                    <ErrorText>
                      {requisiteForm.formState.errors.crossborderPhoneNumber.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {/* VP-201: локальный номер карты для CROSSBORDER_CARD. 13–19 цифр + Luhn (на сервере). */}
              {watchedMethod === "CROSSBORDER_CARD" && (
                <FormGroup>
                  <Label>Локальный номер карты</Label>
                  <Controller
                    name="crossborderCardNumber"
                    control={requisiteForm.control}
                    rules={{
                      required: !isEditingRequisite
                        ? "Номер карты обязателен для CROSSBORDER_CARD"
                        : false,
                      pattern: !isEditingRequisite
                        ? {
                            value: /^[\d\s\-]+$/,
                            message:
                              "Только цифры, пробелы и дефисы",
                          }
                        : undefined,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="4111 1111 1111 1111"
                        hasError={
                          !!requisiteForm.formState.errors.crossborderCardNumber
                        }
                        disabled={isEditingRequisite}
                        title={
                          isEditingRequisite
                            ? "Локальный номер карты нельзя изменить после создания"
                            : ""
                        }
                      />
                    )}
                  />
                  {requisiteForm.formState.errors.crossborderCardNumber && (
                    <ErrorText>
                      {requisiteForm.formState.errors.crossborderCardNumber.message}
                    </ErrorText>
                  )}
                </FormGroup>
              )}

              {watchedMethod === "ACCOUNT_TRANSFER" && (
                <>
                  <FormGroup>
                    <Label>Номер счета</Label>
                    <Controller
                      name="accountNumber"
                      control={requisiteForm.control}
                      rules={{
                        required: !isEditingRequisite ? "Номер счета обязателен" : false,
                        pattern: !isEditingRequisite ? {
                          value: /^\d{20}$/,
                          message: "Номер счета должен содержать 20 цифр",
                        } : undefined,
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          hasError={
                            !!requisiteForm.formState.errors.accountNumber
                          }
                          disabled={isEditingRequisite}
                          title={isEditingRequisite ? "Номер счета нельзя изменить после создания" : ""}
                        />
                      )}
                    />
                    {requisiteForm.formState.errors.accountNumber && (
                      <ErrorText>
                        {requisiteForm.formState.errors.accountNumber.message}
                      </ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label>БИК банка</Label>
                    <Controller
                      name="bankIdCode"
                      control={requisiteForm.control}
                      render={({ field }) => (
                        <Input {...field} disabled={isEditingRequisite} title={isEditingRequisite ? "БИК банка нельзя изменить после создания" : ""} />
                      )}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Имя получателя</Label>
                    <Controller
                      name="recipientName"
                      control={requisiteForm.control}
                      render={({ field }) => (
                        <Input {...field} disabled={isEditingRequisite} title={isEditingRequisite ? "Имя получателя нельзя изменить после создания" : ""} />
                      )}
                    />
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <Label>Мин. сумма</Label>
                <Controller
                  name="minAmount"
                  control={requisiteForm.control}
                  rules={{ required: "Минимальная сумма обязательна" }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      hasError={!!requisiteForm.formState.errors.minAmount}
                    />
                  )}
                />
                {requisiteForm.formState.errors.minAmount && (
                  <ErrorText>
                    {requisiteForm.formState.errors.minAmount.message}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Макс. сумма</Label>
                <Controller
                  name="maxAmount"
                  control={requisiteForm.control}
                  rules={{ required: "Максимальная сумма обязательна" }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      hasError={!!requisiteForm.formState.errors.maxAmount}
                    />
                  )}
                />
                {requisiteForm.formState.errors.maxAmount && (
                  <ErrorText>
                    {requisiteForm.formState.errors.maxAmount.message}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Оборот за все время</Label>
                <Controller
                  name="maxTotalVolumeLimit"
                  control={requisiteForm.control}
                  render={({ field }) => (
                    <Input {...field} />
                  )}
                />
                {isEditingRequisite && fullRequisiteData?.statistics?.allTime && (
                  <UsageText>
                    (Использовано: {fullRequisiteData.statistics.allTime.completedAmount})
                  </UsageText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Платежей за все время</Label>
                <Controller
                  name="paymentLimit"
                  control={requisiteForm.control}
                  render={({ field }) => (
                    <Input 
                      {...field} 
                      type="number" 
                      title="Максимальное общее количество платежей на этот реквизит"
                    />
                  )}
                />
                {isEditingRequisite && fullRequisiteData?.statistics?.allTime && (
                  <UsageText>
                    (Выполнено: {fullRequisiteData.statistics.allTime.completedCount})
                  </UsageText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  Платежей за день
                  <InfoIconWrapper data-tooltip="Обновляется каждый день в 00:00 (МСК)">
                    <InfoIconSVG />
                  </InfoIconWrapper>
                </Label>
                <Controller
                  name="dailyPaymentLimit"
                  control={requisiteForm.control}
                  render={({ field }) => (
                    <Input 
                      {...field} 
                      type="number" 
                      title="Максимальное количество платежей в сутки"
                    />
                  )}
                />
                {isEditingRequisite && fullRequisiteData?.statistics?.today && (
                  <UsageText>
                    (Выполнено: {fullRequisiteData.statistics.today.completedCount})
                  </UsageText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  Макс. параллельных сделок
                  <InfoIconWrapper data-tooltip="Максимальное количество единовременных сделок в статусе 'ожидание'">
                    <InfoIconSVG />
                  </InfoIconWrapper>
                </Label>
                <Controller
                  name="maxParallelOrders"
                  control={requisiteForm.control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      min="1"
                      title="Ограничивает количество одновременных сделок"
                    />
                  )}
                />
              </FormGroup>

              <FormGroup>
                <Label>Интервал между транзакциями (мин)</Label>
                <Controller
                  name="cooldownMin"
                  control={requisiteForm.control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      title="Интервал ожидания между назначениями этого реквизита на заявки в минутах. Пустое поле или 0 = без задержки, 5 = ждет 5 минут после каждой выдачи"
                    />
                  )}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Оборот за день
                  <InfoIconWrapper data-tooltip="Обновляется каждый день в 00:00 (МСК)">
                    <InfoIconSVG />
                  </InfoIconWrapper>
                </Label>
                <Controller
                  name="dailyLimit"
                  control={requisiteForm.control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                    />
                  )}
                />
                {isEditingRequisite && fullRequisiteData?.statistics?.today && (
                  <UsageText>
                    (Использовано: {fullRequisiteData.statistics.today.completedAmount})
                  </UsageText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Название</Label>
                <Controller
                  name="tag"
                  control={requisiteForm.control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={onClose}>
                  Отмена
                </CancelButton>
                <SubmitButton
                  type="submit"
                  disabled={isCreatingRequisite || isUpdatingRequisite}
                >
                  {isCreatingRequisite || isUpdatingRequisite
                    ? isEditingRequisite
                      ? "Сохранение..."
                      : "Создание..."
                    : isEditingRequisite
                      ? "Сохранить"
                      : "Создать реквизит"}
                </SubmitButton>
              </ButtonGroup>
            </Form>
          </FormContainer>
        )}
      </SidebarContent>
    </>
  );
};

export default RequisitesSidebar;
